import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  Plus, Trash2, Edit2, Download, Users, TrendingUp, TrendingDown, PiggyBank,
  DollarSign, Repeat, Tag, Palette, Loader2, Search, Lock, Target, AlertTriangle
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const PRESET_COLORS = ['#22c55e', '#ef4444', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b', '#eab308'];
const EMPTY_FORM = { category: 'expense', description: '', amount: '', date: new Date().toISOString().split('T')[0], frequency: 'one-time', label: '', color: '', tags: '', notes: '' };

const currency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BudgetTracker({ onShare }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_income: 0, total_expenses: 0, total_savings: 0, balance: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showLabelsDialog, setShowLabelsDialog] = useState(false);
  const [newLabel, setNewLabel] = useState({ name: '', color: PRESET_COLORS[0], category: 'expense' });
  const [goals, setGoals] = useState([]);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [goalForm, setGoalForm] = useState({ label: '', monthly_limit: '' });

  const [pieView, setPieView] = useState('expense');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const access = await api.get('/users/modules');
      if (access.data.modules?.budget_tracker === false) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      const [itemsRes, summaryRes, analyticsRes, labelsRes, goalsRes] = await Promise.all([
        api.get('/personal-budget'),
        api.get('/personal-budget/summary'),
        api.get('/personal-budget/analytics'),
        api.get('/personal-budget/labels'),
        api.get('/personal-budget/goals'),
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
      setAnalytics(analyticsRes.data);
      setLabels(labelsRes.data);
      setGoals(goalsRes.data);
    } catch (e) {
      toast.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setForm(EMPTY_FORM); setShowItemDialog(true); };
  const openEdit = (item) => {
    setEditingId(item.budget_id);
    setForm({
      category: item.category,
      description: item.description || '',
      amount: String(item.amount),
      date: item.date || new Date().toISOString().split('T')[0],
      frequency: item.frequency || 'one-time',
      label: item.label || '',
      color: item.color || '',
      tags: (item.tags || []).join(', '),
      notes: item.notes || '',
    });
    setShowItemDialog(true);
  };

  const saveItem = async () => {
    if (!form.description || !form.amount) {
      toast.error('Description and amount are required');
      return;
    }
    const selectedLabel = labels.find(l => l.name === form.label);
    const payload = {
      category: form.category,
      description: form.description,
      amount: parseFloat(form.amount),
      date: form.date,
      frequency: form.frequency,
      label: form.label || null,
      color: form.color || selectedLabel?.color || null,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      notes: form.notes || null,
    };
    try {
      if (editingId) {
        await api.put(`/personal-budget/${editingId}`, payload);
        toast.success('Transaction updated');
      } else {
        await api.post('/personal-budget', payload);
        toast.success(form.frequency === 'one-time' ? 'Transaction added' : 'Recurring transaction created');
      }
      setShowItemDialog(false);
      load();
    } catch (e) {
      toast.error('Failed to save transaction');
    }
  };

  const deleteItem = async (id, isRecurring) => {
    if (!window.confirm(isRecurring ? 'Delete this recurring transaction and all its generated entries?' : 'Delete this transaction?')) return;
    try {
      await api.delete(`/personal-budget/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const addLabel = async () => {
    if (!newLabel.name.trim()) { toast.error('Label name required'); return; }
    try {
      await api.post('/personal-budget/labels', newLabel);
      setNewLabel({ name: '', color: PRESET_COLORS[0], category: 'expense' });
      const res = await api.get('/personal-budget/labels');
      setLabels(res.data);
      toast.success('Label created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create label');
    }
  };

  const deleteLabel = async (id) => {
    try {
      await api.delete(`/personal-budget/labels/${id}`);
      setLabels(labels.filter(l => l.label_id !== id));
      toast.success('Label deleted');
    } catch (e) {
      toast.error('Failed to delete label');
    }
  };

  const saveGoal = async () => {
    if (!goalForm.label || !goalForm.monthly_limit || parseFloat(goalForm.monthly_limit) <= 0) {
      toast.error('Pick a label and a limit greater than 0');
      return;
    }
    try {
      await api.post('/personal-budget/goals', { label: goalForm.label, monthly_limit: parseFloat(goalForm.monthly_limit) });
      toast.success('Spending goal saved');
      setShowGoalDialog(false);
      setGoalForm({ label: '', monthly_limit: '' });
      const res = await api.get('/personal-budget/goals');
      setGoals(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save goal');
    }
  };

  const deleteGoal = async (id) => {
    try {
      await api.delete(`/personal-budget/goals/${id}`);
      setGoals(goals.filter(g => g.goal_id !== id));
      toast.success('Goal removed');
    } catch (e) {
      toast.error('Failed to remove goal');
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get('/personal-budget/export-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'budget-report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="budget-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="budget-access-denied">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <Lock className="h-10 w-10 mx-auto text-muted-foreground" />
            <h2 className="text-xl font-bold">Budget Tracker Unavailable</h2>
            <p className="text-sm text-muted-foreground">This module has been disabled for your account. Contact an administrator for access.</p>
            <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const recurringTemplates = items.filter(i => i.frequency && i.frequency !== 'one-time');
  const transactions = items.filter(i => !i.frequency || i.frequency === 'one-time');
  const allTags = [...new Set(transactions.flatMap(i => i.tags || []))];

  const filtered = transactions.filter(i => {
    if (filterCategory !== 'all' && i.category !== filterCategory) return false;
    if (filterTag !== 'all' && !(i.tags || []).includes(filterTag)) return false;
    if (search && !(`${i.description} ${i.label || ''} ${(i.tags || []).join(' ')}`.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  });

  const pieData = analytics ? (analytics[`${pieView}s_by_label`] || analytics[`${pieView}_by_label`] || []) : [];

  const categoryStyles = {
    income: { text: 'text-green-600', sign: '+', chip: 'bg-green-500/10 border-green-500/20' },
    expense: { text: 'text-red-600', sign: '-', chip: 'bg-red-500/10 border-red-500/20' },
    savings: { text: 'text-blue-600', sign: '', chip: 'bg-blue-500/10 border-blue-500/20' },
  };

  return (
    <div className="min-h-screen bg-background" data-testid="budget-tracker-page">
      <nav className="glass fixed top-0 left-0 right-0 z-50 border-b bg-background/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><PiggyBank className="h-5 w-5" /> Budget Tracker</h2>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="budget-back-btn">← Back to Dashboard</Button>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold">Personal Budget</h1>
            <p className="text-muted-foreground">Track income, expenses & savings with analytics</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {onShare && (
              <Button variant="outline" onClick={onShare} data-testid="share-budget-btn">
                <Users className="h-4 w-4 mr-2" /> Share
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowLabelsDialog(true)} data-testid="manage-labels-btn">
              <Palette className="h-4 w-4 mr-2" /> Labels
            </Button>
            <Button variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Export PDF
            </Button>
            <Button onClick={openAdd} data-testid="add-budget-item-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Transaction
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Income', value: summary.total_income, icon: TrendingUp, color: 'text-green-600', bg: 'from-green-500/10' },
            { label: 'Expenses', value: summary.total_expenses, icon: TrendingDown, color: 'text-red-600', bg: 'from-red-500/10' },
            { label: 'Savings', value: summary.total_savings, icon: PiggyBank, color: 'text-blue-600', bg: 'from-blue-500/10' },
            { label: 'Balance', value: summary.balance, icon: DollarSign, color: summary.balance >= 0 ? 'text-green-600' : 'text-red-600', bg: 'from-purple-500/10' },
          ].map(c => (
            <Card key={c.label} className={`bg-gradient-to-br ${c.bg} to-transparent`} data-testid={`budget-summary-${c.label.toLowerCase()}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <c.icon className={`h-6 w-6 ${c.color}`} />
                  <div>
                    <p className="text-sm text-muted-foreground">{c.label}</p>
                    <p className={`text-xl sm:text-2xl font-bold ${c.color}`}>{currency(c.value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="budget-pie-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Breakdown by Label</CardTitle>
              <Select value={pieView} onValueChange={setPieView}>
                <SelectTrigger className="w-32" data-testid="pie-view-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Expenses</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {pieData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-16" data-testid="pie-empty">No {pieView} data yet. Add transactions with labels to see the breakdown.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={50} paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color || PRESET_COLORS[idx % PRESET_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => currency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card data-testid="budget-bar-card">
            <CardHeader><CardTitle className="text-lg">Monthly Trend (6 months)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={analytics?.monthly || []}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(v) => currency(v)} />
                  <Legend />
                  <Bar dataKey="income" name="Income" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="savings" name="Savings" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Spending Goals */}
        <Card data-testid="goals-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2"><Target className="h-5 w-5" /> Monthly Spending Goals</CardTitle>
            <Button size="sm" variant="outline" onClick={() => { setGoalForm({ label: '', monthly_limit: '' }); setShowGoalDialog(true); }} data-testid="add-goal-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Goal
            </Button>
          </CardHeader>
          <CardContent>
            {goals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="goals-empty">No spending goals yet. Set a monthly limit per label to keep spending on track.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {goals.map(goal => {
                  const labelInfo = labels.find(l => l.name === goal.label);
                  const pct = Math.min(goal.percent || 0, 100);
                  const over = (goal.percent || 0) >= 100;
                  const near = !over && (goal.percent || 0) >= 75;
                  const barColor = over ? 'bg-red-500' : near ? 'bg-amber-500' : 'bg-green-500';
                  return (
                    <div key={goal.goal_id} className={`p-4 rounded-lg border ${over ? 'border-red-500/50 bg-red-500/5' : near ? 'border-amber-500/50 bg-amber-500/5' : ''}`} data-testid={`goal-card-${goal.goal_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="flex items-center gap-2 font-medium">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: labelInfo?.color || '#94a3b8' }} />
                          {goal.label}
                          {over && <Badge variant="destructive" className="text-xs" data-testid={`goal-over-${goal.goal_id}`}><AlertTriangle className="h-3 w-3 mr-1" />Over limit</Badge>}
                          {near && <Badge className="text-xs bg-amber-500 hover:bg-amber-500 text-white" data-testid={`goal-near-${goal.goal_id}`}><AlertTriangle className="h-3 w-3 mr-1" />Near limit</Badge>}
                        </span>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setGoalForm({ label: goal.label, monthly_limit: String(goal.monthly_limit) }); setShowGoalDialog(true); }} data-testid={`edit-goal-${goal.goal_id}`}><Edit2 className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteGoal(goal.goal_id)} data-testid={`delete-goal-${goal.goal_id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                        </div>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        <span className={over ? 'text-red-600 font-semibold' : near ? 'text-amber-600 font-semibold' : ''}>{currency(goal.spent)}</span> of {currency(goal.monthly_limit)} this month ({goal.percent || 0}%)
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recurring */}
        {recurringTemplates.length > 0 && (
          <Card data-testid="recurring-section">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Repeat className="h-5 w-5" /> Recurring Transactions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {recurringTemplates.map(item => (
                <div key={item.budget_id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30" data-testid={`recurring-item-${item.budget_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {item.color && <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} />}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{item.description}</p>
                      <p className="text-xs text-muted-foreground capitalize">{item.frequency} • {item.label || item.category} • next: {item.next_occurrence || item.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`font-bold ${categoryStyles[item.category]?.text}`}>{categoryStyles[item.category]?.sign}{currency(item.amount)}</span>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`edit-recurring-${item.budget_id}`}><Edit2 className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteItem(item.budget_id, true)} data-testid={`delete-recurring-${item.budget_id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Transactions */}
        <Card data-testid="transactions-section">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
              <CardTitle className="text-lg">Transactions ({filtered.length})</CardTitle>
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9 w-44" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="budget-search-input" />
                </div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-32" data-testid="filter-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
                {allTags.length > 0 && (
                  <Select value={filterTag} onValueChange={setFilterTag}>
                    <SelectTrigger className="w-32" data-testid="filter-tag-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tags</SelectItem>
                      {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10" data-testid="transactions-empty">No transactions found. Click "Add Transaction" to get started.</p>
            ) : (
              <div className="space-y-2">
                {filtered.map(item => (
                  <div key={item.budget_id} className={`flex items-center justify-between p-3 rounded-lg border ${categoryStyles[item.category]?.chip}`} data-testid={`transaction-${item.budget_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{item.description}</p>
                          {item.label && (
                            <Badge variant="outline" className="text-xs" style={{ borderColor: item.color || undefined, color: item.color || undefined }}>
                              <span className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: item.color || '#94a3b8' }} />{item.label}
                            </Badge>
                          )}
                          {item.auto_generated && <Badge variant="secondary" className="text-xs"><Repeat className="h-3 w-3 mr-1" />auto</Badge>}
                          {(item.tags || []).map(t => (
                            <Badge key={t} variant="secondary" className="text-xs"><Tag className="h-3 w-3 mr-1" />{t}</Badge>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">{item.date}{item.notes ? ` • ${item.notes}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`font-bold ${categoryStyles[item.category]?.text}`}>{categoryStyles[item.category]?.sign}{currency(item.amount)}</span>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`edit-transaction-${item.budget_id}`}><Edit2 className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteItem(item.budget_id, false)} data-testid={`delete-transaction-${item.budget_id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="budget-item-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="item-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Amount</Label>
                <Input type="number" step="0.01" placeholder="0.00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="item-amount-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input placeholder="e.g. Groceries at Walmart" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="item-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="item-date-input" />
              </div>
              <div className="space-y-1">
                <Label>Repeat</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="item-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-time</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Label / Category</Label>
              <Select value={form.label || 'none'} onValueChange={(v) => {
                const l = labels.find(x => x.name === v);
                setForm({ ...form, label: v === 'none' ? '' : v, color: l?.color || form.color });
              }}>
                <SelectTrigger data-testid="item-label-select"><SelectValue placeholder="No label" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No label</SelectItem>
                  {labels.map(l => (
                    <SelectItem key={l.label_id} value={l.name}>
                      <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: l.color }} />{l.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button type="button" className="text-xs text-primary underline" onClick={() => setShowLabelsDialog(true)} data-testid="create-label-link">+ Create new label</button>
            </div>
            <div className="space-y-1">
              <Label>Tags (comma separated)</Label>
              <Input placeholder="e.g. family, essentials" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="item-tags-input" />
            </div>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="item-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={saveItem} data-testid="save-item-btn">{editingId ? 'Save Changes' : 'Add Transaction'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goal Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="max-w-sm" data-testid="goal-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5" /> Monthly Spending Goal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Label</Label>
              <Select value={goalForm.label || undefined} onValueChange={(v) => setGoalForm({ ...goalForm, label: v })}>
                <SelectTrigger data-testid="goal-label-select"><SelectValue placeholder="Choose a label" /></SelectTrigger>
                <SelectContent>
                  {labels.filter(l => l.category !== 'income').map(l => (
                    <SelectItem key={l.label_id} value={l.name}>
                      <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: l.color }} />{l.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {labels.filter(l => l.category !== 'income').length === 0 && (
                <p className="text-xs text-muted-foreground">No expense labels yet — create one via the Labels button first.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Monthly limit ($)</Label>
              <Input type="number" step="0.01" min="0" placeholder="e.g. 400" value={goalForm.monthly_limit} onChange={(e) => setGoalForm({ ...goalForm, monthly_limit: e.target.value })} data-testid="goal-limit-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoalDialog(false)}>Cancel</Button>
            <Button onClick={saveGoal} data-testid="save-goal-btn">Save Goal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Labels Dialog */}
      <Dialog open={showLabelsDialog} onOpenChange={setShowLabelsDialog}>
        <DialogContent className="max-w-md" data-testid="labels-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Palette className="h-5 w-5" /> Manage Labels</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Input placeholder="Label name (e.g. Groceries)" value={newLabel.name} onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })} data-testid="new-label-name-input" />
              <div className="flex items-center gap-2 flex-wrap">
                {PRESET_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setNewLabel({ ...newLabel, color: c })}
                    className={`w-6 h-6 rounded-full border-2 ${newLabel.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} data-testid={`color-swatch-${c.replace('#', '')}`} />
                ))}
                <input type="color" value={newLabel.color} onChange={(e) => setNewLabel({ ...newLabel, color: e.target.value })} className="w-8 h-8 rounded cursor-pointer bg-transparent" data-testid="custom-color-input" />
              </div>
              <div className="flex gap-2">
                <Select value={newLabel.category} onValueChange={(v) => setNewLabel({ ...newLabel, category: v })}>
                  <SelectTrigger className="w-32" data-testid="new-label-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={addLabel} className="flex-1" data-testid="add-label-btn"><Plus className="h-4 w-4 mr-1" /> Add Label</Button>
              </div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {labels.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4" data-testid="labels-empty">No labels yet. Create one above.</p>
              ) : labels.map(l => (
                <div key={l.label_id} className="flex items-center justify-between p-2 rounded-lg border" data-testid={`label-row-${l.label_id}`}>
                  <span className="flex items-center gap-2 text-sm">
                    <span className="w-4 h-4 rounded-full" style={{ backgroundColor: l.color }} />
                    {l.name}
                    <Badge variant="secondary" className="text-xs capitalize">{l.category}</Badge>
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => deleteLabel(l.label_id)} data-testid={`delete-label-${l.label_id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
